import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import OrderTypeEnum from "@/constants/enums/OrderTypeEnum";
import { DEFAULT_ORDERS_PAGE_SIZE, useOrderStore } from "@/stores/ordersStore";
import { OrderStatus } from "@/types";
import { HIDDEN_ORDER_STATUSES } from "../const/orderStatuses";

export const ORDERS_FILTER_SESSION_KEY = "orders_filters_v1";

/**
 * Keeps the orders filter, sort and pagination state in step with the URL and session storage.
 *
 * Lives in a hook (not in the filter inputs) because the inputs sit in a mobile drawer that is not
 * mounted until it is opened. Call it once from a component that is always mounted with the list.
 */
export const useOrdersFilterSync = () => {
  const sortDirection = useOrderStore((state) => state.sortDirection);
  const setSortDirection = useOrderStore((state) => state.setSortDirection);
  const sortBy = useOrderStore((state) => state.sortBy);
  const setSortBy = useOrderStore((state) => state.setSortBy);
  const orderTypes = useOrderStore((state) => state.orderTypes);
  const setOrderTypes = useOrderStore((state) => state.setOrderTypes);
  const statuses = useOrderStore((state) => state.statuses);
  const setStatuses = useOrderStore((state) => state.setStatuses);
  const salesItemId = useOrderStore((state) => state.salesItemId);
  const setSalesItemId = useOrderStore((state) => state.setSalesItemId);
  const currentPage = useOrderStore((state) => state.currentPage);
  const setPage = useOrderStore((state) => state.setPage);
  const limit = useOrderStore((state) => state.limit);
  const setLimit = useOrderStore((state) => state.setLimit);

  /* Get filter, sort and pagination options from URL or session storage */
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const statusParams = searchParams.getAll("status");
    const typeParams = searchParams.getAll("type");
    const salesItem = searchParams.get("salesItem");
    const sort = searchParams.get("sort");
    const dir = searchParams.get("dir");
    const page = searchParams.get("page");
    const pageLimit = searchParams.get("limit");

    // The legacy inactive / excludeContract / awaiting params are deliberately not read. An old
    // bookmark carrying only those counts as "no params", so we fall back to session storage and the
    // write-back effect below rewrites the URL without them.
    const hasParams = Boolean(
      statusParams.length > 0 || typeParams.length > 0 || salesItem || sort || dir || page || pageLimit
    );

    let paramsToUse = searchParams;
    let isPersisted = hasParams;

    if (!hasParams) {
      try {
        const saved = sessionStorage.getItem(ORDERS_FILTER_SESSION_KEY);
        if (saved) {
          const savedSP = new URLSearchParams(saved);
          paramsToUse = savedSP as unknown as URLSearchParams; // use for initialization below
          isPersisted = true;
          setSearchParams(savedSP, { replace: true });
        }
      } catch {
        // ignored
      }
    }

    // On a truly virgin load (no URL params, nothing saved) leave the store defaults untouched.
    if (!isPersisted) return;

    const statusParams2 = paramsToUse.getAll("status");
    const typeParams2 = paramsToUse.getAll("type");
    const salesItem2 = paramsToUse.get("salesItem");
    const sort2 = paramsToUse.get("sort");
    const dir2 = paramsToUse.get("dir");
    const page2 = paramsToUse.get("page");
    const pageLimit2 = paramsToUse.get("limit");

    // Once we are hydrating from a persisted source, it is the source of truth —
    // absence of a param means "cleared", not "fall back to the store default".
    // Statuses that are no longer offered are dropped for the same reason AUCTION is below:
    // the dropdown would render a pill the user cannot see in the list or clear.
    setStatuses(statusParams2.filter((status) => !HIDDEN_ORDER_STATUSES.includes(status)) as OrderStatus[]);
    // AUCTION is no longer selectable, so drop it from old links rather than rendering a pill for a
    // value the dropdown can no longer show or clear.
    const hydratedTypes = typeParams2.map(Number).filter((type) => type !== OrderTypeEnum.AUCTION);
    setOrderTypes(hydratedTypes.length > 0 ? hydratedTypes : null);
    setSalesItemId(salesItem2 ? Number(salesItem2) : null);
    setSortBy(sort2 || "date");
    setSortDirection(dir2?.toUpperCase() === "ASC" ? "ASC" : "DESC");
    // Must stay last: every setter above resets currentPage to 1.
    setPage(page2 ? Number(page2) : 1);
    setLimit(pageLimit2 ? Number(pageLimit2) : DEFAULT_ORDERS_PAGE_SIZE);
  }, []);

  /* Set filter, sort and pagination options to URL and session storage */
  useEffect(() => {
    const sp = new URLSearchParams();
    if (statuses && statuses.length > 0) {
      statuses.forEach((s) => sp.append("status", s));
    }
    if (orderTypes && orderTypes.length > 0) {
      orderTypes.forEach((t) => sp.append("type", String(t)));
    }
    if (salesItemId) sp.set("salesItem", String(salesItemId));
    if (sortBy) sp.set("sort", sortBy);
    if (sortDirection) sp.set("dir", sortDirection.toLowerCase());
    if (currentPage) sp.set("page", String(currentPage));
    if (limit) sp.set("limit", String(limit));

    setSearchParams(sp, { replace: true });

    try {
      sessionStorage.setItem(ORDERS_FILTER_SESSION_KEY, sp.toString());
    } catch {
      // ignore
    }
  }, [statuses, orderTypes, salesItemId, sortBy, sortDirection, currentPage, limit, setSearchParams]);
};

export default useOrdersFilterSync;
